import { KenticoConfig, StorybookRuntime, StorybookRuntimeState } from '@jellyvision/storybook-runtime';
import { MomentAttributes } from '@jellyvision/storybook-runtime/dist/StorybookRuntime/adapters';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  MomentSidebarContainer,
  StorybookRuntimeWrapper,
  MomentPageLayoutWrapper,
  RatingsContainer,
  StorybookRuntimeContainer,
  SpinnerWrapper,
} from 'Containers/MomentPage/styled';
import Feedback from 'Containers/ReviewPage/Feedback';
import { ContentfulMomentSidebar } from 'ContentfulDefaults/types/momentSidebar';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useTextContext } from 'Contexts/textContext';
import { Spinner } from 'DesignLibrary/atoms';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import { BuilderGoSettings } from 'Utils/apiTypes';
import { sendInsightsEvent } from 'Utils/insights';
import Logger from 'Utils/logger';

import { talkspaceURL } from './constants';
import { Footer } from './Footer';
import MtmError from './MtmError';
import { MomentSidebar } from './Sidebar';
import TalkspaceTestimonials from './TalkspaceTestimonials';
import TeladocTestimonials from './TeladocTestimonials';

export interface MomentProps {
  topic: string;
  codename: string | null;
  client: string;
  handleFeedbackSubmit: () => void;
  feedbackSubmitted: boolean;
  publicationsGoSettings?: BuilderGoSettings | null;

  // updateJVPUserUiSettings: (uiSettings: Partial<DirectlyUpdateableJVPUserAttributes>) => void;
}
export const Moment = ({
  codename,
  topic,
  client,
  handleFeedbackSubmit,
  feedbackSubmitted,
  publicationsGoSettings,
}: MomentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { is_go_teladoc_mtm_enabled } = useFeatureFlagContext();
  const { retrieveContentfulData } = useTextContext();
  const [showTestimonials, setShowTestimonials] = React.useState<boolean>(false);
  const [autoPlay, setAutoPlay] = React.useState<boolean>(false);
  const momentSidebarData = retrieveContentfulData<ContentfulMomentSidebar>('moments_that_matter');

  const momentTopic = (searchParams.get('topic') || topic || '').toLowerCase();
  const momentCodename = searchParams.get('moment') || codename;

  const [videoStarted, setVideoStarted] = React.useState<boolean>(false);
  // we're using a wrapper to make all Moment events have the MTM product baked into it rather than simply updating the insight event default product setting
  // if we set the default product value here, that setting would stick elsewhere and events coming from non-MTM parts of picwell app would have that product associated with them
  const sendInsightsEventWithProduct = (eventName: string, eventAttributes?: MomentAttributes): void => {
    sendInsightsEvent(null, eventName, {
      product: topic || '',
      ...eventAttributes,
    });
  };
  const [isMuted, setIsMuted] = useState(false);
  const [closedCaptions, setClosedCaptions] = useState(false);
  const runtimeState = React.useMemo<StorybookRuntimeState>(
    () => ({
      cc: closedCaptions,
      muted: isMuted,
      autoPlay,
    }),
    [showTestimonials, codename, autoPlay, isMuted, closedCaptions],
  );

  useEffect(() => {
    if (showTestimonials) {
      searchParams.delete('moment');
      setSearchParams(searchParams);
    }
  }, [showTestimonials, searchParams, setSearchParams]);

  // Show spinner while momentSidebarData is loading
  if (!momentSidebarData || !publicationsGoSettings) {
    return (
      <MomentPageLayoutWrapper topic={momentTopic}>
        <PageLayout>
          <SpinnerWrapper>
            <div data-testid="spinner">
              <Spinner />
            </div>
          </SpinnerWrapper>
        </PageLayout>
      </MomentPageLayoutWrapper>
    );
  }

  const isTalkspace = momentTopic === 'talkspace';

  const allowedTopicsList = Array.isArray(momentSidebarData?.moment_sidebars)
    ? momentSidebarData.moment_sidebars.map((item) => item.topic?.toLowerCase()).filter(Boolean)
    : [];
  const hasAccess = isTalkspace
    ? publicationsGoSettings?.has_talkspace_support_page
    : is_go_teladoc_mtm_enabled && allowedTopicsList?.includes(momentTopic);

  if (!hasAccess || !momentSidebarData) {
    return <MtmError />;
  }

  // const { audioState } = useAudioContext();

  // TODO: Handle moment enabled/disabled
  //   New selector to determine publication based on plan year
  //   and return the associated go_settings toggle
  // const planYear = searchParams.get('planYear');
  // const enabled = makeGetMomentEnabled(planYear, topic);
  const runtimeContent = {
    codename: momentCodename || '',
    module: momentTopic,
    customerKey: client,
  };

  const kenticoEnvironmentId = localStorage.getItem('kenticoEnvironmentId');
  const usePreviewMode = localStorage.getItem('usePreviewMode') === 'true';
  const kenticoConfig: KenticoConfig = {
    usePreviewMode,
    environment: {
      environmentId: kenticoEnvironmentId || '',
    },
  };

  // const handleToggleCC = () => {
  //   updateJVPUserUiSettings({
  //     closed_captioned: !audioState.areClosedCaptionsEnabled,
  //   });
  // };

  // const handleToggleMute = () => {
  //   const newMuteState = !audioState.isMuted;
  //   const newClosedCaptionState = newMuteState ? true : audioState.areClosedCaptionsEnabled;
  //   updateJVPUserUiSettings({
  //     audio_muted: newMuteState,
  //     closed_captioned: newClosedCaptionState,
  //   });
  // };

  const goToNextMoment = (codename: string) => {
    searchParams.set('moment', codename);
    setSearchParams(searchParams);
  };

  const handleModuleEnd = () => {
    setShowTestimonials(true);
    sendInsightsEventWithProduct('module_end');
  };

  return (
    <MomentPageLayoutWrapper topic={momentTopic}>
      <PageLayout>
        <StorybookRuntimeContainer>
          <StorybookRuntimeWrapper>
            {!showTestimonials && (
              <StorybookRuntime
                data-testid="storybook-runtime"
                key={codename || 'default'}
                content={runtimeContent}
                kenticoConfig={kenticoConfig}
                state={runtimeState}
                externalAdapters={{
                  onMomentRender: (momentAttributes: MomentAttributes) => {
                    sendInsightsEventWithProduct('moment_shown', momentAttributes);
                  },
                  onCodenameChange: (nextCodename: string) => goToNextMoment(nextCodename),
                  onActionTrigger: () => {},
                  onUserStateChange: () => {},
                  onUiStateChange: (key, value) => {
                    // Make sure we trigger this only once
                    // videoStarted is being used as a flag to know user has started playing the video
                    if (key === 'playbackStatus' && value === 'playing' && !videoStarted) {
                      setVideoStarted(true);
                      setAutoPlay(true);
                      sendInsightsEventWithProduct('module_start');
                    }
                    if (key === 'muted') {
                      setIsMuted(Boolean(value));
                    }
                    if (key === 'cc') {
                      setClosedCaptions(Boolean(value));
                    }
                  },
                  onModuleEnded: () => handleModuleEnd(),
                  onErrorNotify: (err) => {
                    Logger.error(err);
                  },
                }}
              />
            )}
            {showTestimonials && !isTalkspace && (
              <TeladocTestimonials
                setShowTestimonials={setShowTestimonials}
                brand="teladoc"
                testimonials={publicationsGoSettings?.teladoc_testimonials || []}
              />
            )}
            {showTestimonials && isTalkspace && (
              <TalkspaceTestimonials
                setShowTestimonials={setShowTestimonials}
                brand="talkspace"
                testimonials={publicationsGoSettings?.talkspace_testimonials || []}
              />
            )}
            <MomentSidebarContainer>
              {momentSidebarData && (
                <MomentSidebar
                  content={momentSidebarData}
                  topic={momentTopic}
                  button_link={
                    isTalkspace ? talkspaceURL : publicationsGoSettings?.health_outcomes_teladoc_url
                  }
                  signup_code={
                    !isTalkspace ? publicationsGoSettings?.health_outcomes_vendor_sign_up_code : ''
                  }
                  sendAnalyticsEvent={sendInsightsEventWithProduct}
                />
              )}
            </MomentSidebarContainer>
          </StorybookRuntimeWrapper>
        </StorybookRuntimeContainer>
        <RatingsContainer>
          <Feedback
            feedbackSubmitted={feedbackSubmitted}
            handleFeedbackSubmit={handleFeedbackSubmit}
            page="mtm"
          />
        </RatingsContainer>

        <PageLayout bg="--background-white" page="landing-footer">
          <Footer />
        </PageLayout>
      </PageLayout>
    </MomentPageLayoutWrapper>
  );
};
